import React from "react";
import { Modal } from "@material-ui/core";
import { Formik } from "formik";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import FormField from "../FormField/FormField";
import { PrimaryCTAButton, PrimaryOutlinedCloseCTAButton } from "../Buttons";
import styled from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { GenerateInvoiceValidationSchema } from "../../../helpers/validationSchema";
import CaseService from "../../../services/CaseService";
import { CustomRadio } from "../FormInputs";

const GenerateInvoiceModal = ({ invoiceModal, setInvoiceModal, caseId }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const inputs = [
    {
      name: "feeType",
      type: "text",
      label: "Fees Type",
      required: true,
    },
    {
      name: "amount",
      type: "numberFormat",
      label: "Amount",
      required: true,
    },
  ];

  const claimant_percentage = [
    {
      name: "claimantPercentage",
      type: "number",
      label: "Claimant Percentage",
      required: true,
    },
  ];

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "genrate invoice..." });
      const response = await CaseService.generateInvoiceCase(
        invoiceModal?.caseId,
        {
          ...values,
          amount: Number(values.amount?.toString()?.split(",")?.join("")),
        },
      );
      if (response) {
        enqueueSnackbar(response.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setInvoiceModal({ state: false });
    }
  }

  return (
    <Modal
      open={invoiceModal?.state}
      onClose={() => setInvoiceModal({ state: false })}
      disableRestoreFocus
    >
      <DrawerContainer>
        <ModalOverlay onClick={() => setInvoiceModal({ state: false })} />
        <ModalContent>
          <HeadingContainer>
            <HeaderLeft>
              <HeaderText>
                <Heading>Generate Invoice</Heading>
                <Subheading>
                  Fill in the details to create a new invoice
                </Subheading>
              </HeaderText>
            </HeaderLeft>
            <CloseButton
              onClick={() => setInvoiceModal({ state: false })}
              aria-label="Close modal"
            >
              <img
                src={require("../../../assets/images/closeModal.svg")}
                alt="close"
              />
            </CloseButton>
          </HeadingContainer>

          <Divider />

          <Formik
            initialValues={{
              feeType: "",
              amount: 0,
              claimantPercentage: 0 || "",
              incluseGst: false,
            }}
            enableReinitialize
            validateOnBlur
            validationSchema={GenerateInvoiceValidationSchema}
            validateOnChange
            onSubmit={onFormSubmit}
          >
            {({
              values,
              handleChange,
              errors,
              handleBlur,
              touched,
              handleSubmit,
              setFieldValue,
            }) => (
              <>
                <FormContainer>
                  <FormSection>
                    <SectionTitle>Invoice Details</SectionTitle>
                    <StyledFormElementsWrapper className="disabledArrows">
                      {inputs.map((input, index) => (
                        <FormFieldWrapper key={index}>
                          <FormField
                            {...{
                              input,
                              values,
                              handleBlur,
                              handleChange,
                              touched,
                              errors,
                            }}
                          />
                        </FormFieldWrapper>
                      ))}
                    </StyledFormElementsWrapper>
                  </FormSection>

                  <FormSection>
                    <SectionTitle>Payment Distribution</SectionTitle>
                    <StyledFormElementsWrapper>
                      {claimant_percentage.map((input, index) => (
                        <FormFieldWrapper key={index}>
                          <FormField
                            {...{
                              input,
                              values,
                              handleBlur,
                              handleChange,
                              touched,
                              errors,
                            }}
                          />
                        </FormFieldWrapper>
                      ))}
                    </StyledFormElementsWrapper>
                  </FormSection>

                  <FormSection>
                    <SectionTitle>Tax Options</SectionTitle>
                    <GSTContainer>
                      <CustomRadio
                        radioInputs={[
                          { label: "Include GST", value: "incluseGst" },
                        ]}
                        name={"incluseGst"}
                        handleChange={(e) => handleChange(e, "incluseGst")}
                        {...{ values, setFieldValue }}
                      />
                      <GSTNote>GST will be added to the invoice amount</GSTNote>
                    </GSTContainer>
                  </FormSection>

                  <Divider />
                </FormContainer>
                <ButtonContainer>
                  <PrimaryOutlinedCloseCTAButton
                    style={{ width: "auto" }}
                    onClick={() => setInvoiceModal({ state: false })}
                  >
                    Cancel
                  </PrimaryOutlinedCloseCTAButton>
                  <PrimaryCTAButton
                    style={{ width: "auto" }}
                    onClick={handleSubmit}
                  >
                    Generate Invoice
                  </PrimaryCTAButton>
                </ButtonContainer>
              </>
            )}
          </Formik>
        </ModalContent>
      </DrawerContainer>
    </Modal>
  );
};

export default GenerateInvoiceModal;

export const DrawerContainer = styled.div`
  max-width: 95%;
  width: 100%;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 8px;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 580px;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
`;

export const ModalContent = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const HeadingContainer = styled.div`
  padding: 28px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 22px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  line-height: 1.2;
`;

export const Subheading = styled.p`
  margin: 0;
  padding: 0;
  font-size: 14px;
  color: ${COLORS.INPUT_LABEL};
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

export const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e8e8;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: linear-gradient(to right, transparent, #e0e0e0, transparent);
  margin: 0 20px;
`;

export const FormContainer = styled.div`
  padding: 24px 32px 32px;
  height: 410px;
  overflow: auto;
`;

export const FormSection = styled.div`
  margin-bottom: 28px;

  &:last-of-type {
    margin-bottom: 24px;
  }
`;

export const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: ${COLORS.INPUT_LABEL};
  letter-spacing: 0.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

export const StyledFormElementsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormFieldWrapper = styled.div`
  position: relative;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateX(2px);
  }
`;

const GSTContainer = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  border: 2px solid #e8e8e8;
  transition: all 0.2s ease;
`;

const GSTNote = styled.p`
  margin: 12px 0 0 32px;
  font-size: 13px;
  color: ${COLORS.INPUT_LABEL};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-style: italic;
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding: 24px 32px 32px;

  button {
    min-width: 140px;
    padding: 14px 24px !important;
    border-radius: 8px;
    font-weight: 600;
    font-size: 15px;
    transition: all 0.2s ease;
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

export const StyledFormElementsWrapper1 = styled.div`
  margin-bottom: 28px;
  & .link {
    margin-top: 10px;
    display: flex;
    justify-content: flex-start;
  }
  & .waring-msg {
    color: ${COLORS.WARNING_COLOR};
    font-size: 10px;
    white-space: nowrap;
  }
`;
